/* eslint-disable */
"use client";

import { useEffect, useMemo, useState } from "react";
import { cn } from "./utils";

const EASE = "cubic-bezier(0.13, 0.85, 0.18, 1)";

/**
 * Slot-machine number. Every digit is a vertical reel that spins on first paint
 * and eases to a stop on its real value — left digits settle first, right digits
 * keep spinning a little longer.
 *
 * The reel is built so that offset 0 already shows the correct digit (each reel
 * holds a whole number of 0–9 cycles), which keeps SSR output, hydration and
 * reduced-motion fallbacks all showing the true value.
 */
export function RollingNumber({
  text,
  className,
  duration = 1150,
  delay = 0,
}: {
  text: string;
  className?: string;
  duration?: number;
  delay?: number;
}) {
  const [rolled, setRolled] = useState(false);

  const parts = useMemo(() => {
    let digitIndex = 0;
    return text.split("").map((char) => {
      if (char < "0" || char > "9") return { type: "static" as const, char };
      const target = Number(char);
      const i = digitIndex++;
      const spins = 2 + Math.min(i, 3); // 2–5 full rotations
      const steps = spins * 10;
      const items = Array.from({ length: steps + 1 }, (_, k) => (target + k) % 10);
      return {
        type: "reel" as const,
        items,
        steps,
        ms: duration + i * 95, // staggered stop, left to right
      };
    });
  }, [text, duration]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    const id = window.setTimeout(() => setRolled(true), 60 + delay);
    return () => window.clearTimeout(id);
  }, [delay]);

  return (
    <span className={cn("roll", className)} aria-label={text}>
      {parts.map((part, i) =>
        part.type === "static" ? (
          <span key={i} className="roll-cell" aria-hidden>
            {part.char === " " ? "\u00A0" : part.char}
          </span>
        ) : (
          <span key={i} className="roll-window" aria-hidden>
            <span
              className="roll-reel block"
              style={{
                transform: `translateY(-${rolled ? part.steps : 0}em)`,
                transition: rolled ? `transform ${part.ms}ms ${EASE}` : undefined,
              }}
            >
              {part.items.map((d, k) => (
                <span key={k} className="roll-cell">
                  {d}
                </span>
              ))}
            </span>
          </span>
        )
      )}
    </span>
  );
}
